import { useTranslate } from '@tolgee/react';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import { LogoSvg } from '../../../assets/icons';
import AppreciateSvg from '../../../assets/icons/AppreciateSvg';
import FoldedHandsSvg from '../../../assets/icons/FoldedHandsSvg';
import StarStruckSvg from '../../../assets/icons/StarStruckSvg';
import theme from '../../../assets/theme';
import { AuthError } from '../../../types/auth';
import { AUTH_ERROR_META, AuthErrorIconKey } from '../../../utils/authErrorMap';
import { readPostAuthRedirect } from '../../../utils/postAuthRedirect';
import BackgroundBlobs from '../../_molecules/BackgroundBlobs/BackgroundBlobs';
import { MailIco } from './AuthIcons';
import { CenterWrap, PrimaryBtn, rise, shadowCard } from './authStyles';

const ICONS: Record<AuthErrorIconKey, React.FC> = {
  starStruck: StarStruckSvg,
  foldedHands: FoldedHandsSvg,
  appreciate: AppreciateSvg,
  mail: () => <MailIco />,
};

/**
 * Full-page auth error in the centered-card design. Rendered by
 * `pages/auth/error.tsx`. Copy + primary CTA come from `AUTH_ERROR_META`; a
 * "Contact support" link is always offered, with the correlation id when present.
 */
const AuthErrorView: React.FC<{ error: AuthError }> = ({ error }) => {
  const router = useRouter();
  const { t } = useTranslate();
  const { showNewMessages } = useIntercom();
  const [copied, setCopied] = useState(false);

  const meta = AUTH_ERROR_META[error.type] ?? AUTH_ERROR_META.unknown;
  const Icon = ICONS[meta.iconKey];

  const copyRef = async () => {
    if (!error.correlationId) return;
    try {
      await navigator.clipboard.writeText(error.correlationId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard may be unavailable (insecure context) — the id is still visible
    }
  };

  const withRedirect = (path: string) => {
    const stored = readPostAuthRedirect();
    return stored ? `${path}?redirect=${encodeURIComponent(stored)}` : path;
  };

  const handlePrimary = () => {
    switch (meta.action) {
      case 'go-signup':
        router.push(withRedirect('/signup'));
        return;
      case 'go-home':
        router.push('/');
        return;
      case 'contact-support':
        handleContactSupport();
        return;
      case 'retry-login':
      default:
        router.push(withRedirect('/login'));
    }
  };

  const handleContactSupport = () => {
    const message = `Hello! I'm running into a problem signing in (${error.type}).${
      error.correlationId ? `\nCorrelation ID: ${error.correlationId}` : ''
    }${error.message ? `\nDetails: ${error.message}` : ''}\n\nCould you help?`;
    showNewMessages(message);
  };

  return (
    <Root>
      <Pane>
        <BackgroundBlobs zIndex={0} />
        <Card>
          <LogoWrap>
            <LogoSvg />
          </LogoWrap>

          <CenterWrap>
            <IconWrap>
              <Icon />
            </IconWrap>
            <h2>{t(meta.titleKey, meta.titleDefault)}</h2>
            <p>{error.message || t(meta.bodyKey, meta.bodyDefault)}</p>
          </CenterWrap>

          <Actions>
            <PrimaryBtn onClick={handlePrimary}>
              {t(meta.ctaKey, meta.ctaDefault)}
            </PrimaryBtn>
            <SupportLine>
              {t('auth.err.supportPre', 'Still stuck?')}{' '}
              <button type="button" onClick={handleContactSupport}>
                {t('auth.err.contactSupport', 'Contact support')}
              </button>
            </SupportLine>
            {error.correlationId && (
              <RefBox>
                <RefLabel>
                  {t(
                    'auth.err.refShare',
                    'Share this reference with support (a screenshot works):',
                  )}
                </RefLabel>
                <RefRow>
                  <code>{error.correlationId}</code>
                  <button type="button" onClick={copyRef}>
                    {copied
                      ? t('auth.err.copied', 'Copied ✓')
                      : t('auth.err.copy', 'Copy')}
                  </button>
                </RefRow>
              </RefBox>
            )}
          </Actions>
        </Card>
      </Pane>
    </Root>
  );
};

const Root = styled.div`
  position: relative;
  min-height: 100vh;
  display: flex;
`;

const Pane = styled.section`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${theme.colors.background};
  padding: 64px 20px;

  @media (max-width: 900px) {
    padding: 64px 18px 40px;
  }
`;

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 432px;
  background: #fff;
  padding: 44px 40px 36px;
  border-radius: 18px;
  box-shadow: ${shadowCard};
  animation: ${rise} 0.5s cubic-bezier(0.645, 0.045, 0.355, 1);

  @media (max-width: 900px) {
    padding: 38px 26px 30px;
  }
`;

const LogoWrap = styled.div`
  display: block;
  width: max-content;
  margin: 0 auto 26px;
  color: ${theme.colors.orange};

  svg {
    height: 30px;
    width: auto;
    display: block;
  }
`;

const IconWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 14px;

  svg {
    width: 84px;
    height: 84px;
    color: ${theme.colors.orange};
  }
`;

const Actions = styled.div`
  margin-top: 26px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SupportLine = styled.div`
  text-align: center;
  font-size: 13.5px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.5);

  button {
    color: ${theme.colors.orangeDark};
    font-weight: 800;
    padding: 0;
    font-size: inherit;
    background: none;
    border: none;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const RefBox = styled.div`
  margin-top: 4px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.grey};
`;

const RefLabel = styled.div`
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.5);
  margin-bottom: 6px;
  text-align: center;
`;

const RefRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  code {
    font-family: monospace;
    font-size: 12.5px;
    font-weight: 700;
    color: ${theme.colors.black};
    word-break: break-all;
  }
  button {
    flex: 0 0 auto;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 800;
    color: ${theme.colors.orangeDark};
    &:hover {
      text-decoration: underline;
    }
  }
`;

export default AuthErrorView;
